import { Copy } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/useToast';

interface CodeBlockProps {
  value: unknown;
  language?: 'json' | 'text';
  maxHeight?: number | string;
  className?: string;
}

export function CodeBlock({ value, language = 'json', maxHeight = 360, className }: CodeBlockProps) {
  const body = React.useMemo(() => {
    if (language === 'json' && typeof value !== 'string') {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    }
    return typeof value === 'string' ? value : String(value);
  }, [value, language]);

  const onCopy = () => {
    void navigator.clipboard.writeText(body).then(() => toast({ title: 'Copied to clipboard' }));
  };

  return (
    <div className={cn('relative overflow-hidden rounded-md border bg-muted/30', className)}>
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-1.5 top-1.5 h-7 w-7 opacity-80 hover:opacity-100"
        onClick={onCopy}
        aria-label="Copy"
      >
        <Copy className="h-3.5 w-3.5" />
      </Button>
      <pre
        className="scrollbar-thin overflow-auto p-3 pr-10 font-mono text-xs leading-relaxed text-foreground"
        style={{ maxHeight }}
      >
        {body}
      </pre>
    </div>
  );
}
